import React from 'react';
import './Tabs.css'
import { Link, useLocation } from 'react-router-dom';
import { useIsAdmin, useIsAppAdmin } from '../../../context/UserProfileContext';

interface Tab {
  id: TabId;
  label: string;
  link: string;
  adminOnly?: boolean;
  appAdminOnly?: boolean;
}

export type TabId = 'inputEdit' | 'weekly' | 'masterManage' | 'classificationMarket' | 'enlightSyncCheck';

interface TabsProps {
  disabledTabs?: Partial<Record<TabId, boolean>>;
  disabledReasons?: Partial<Record<TabId, string>>;
}

const tabs: Tab[] = [
  { id: 'inputEdit', label: '入力/修正', link: '/' },
  { id: 'weekly', label: '週報連', link: '/weekly-report' },
  { id: 'masterManage', label: 'マスタ管理', link: '/master-manage' },
  // 分類市場は管理者限定機能のため、一般ユーザーにはタブ自体を表示しない
  { id: 'classificationMarket', label: '分類市場', link: '/classification-market', adminOnly: true },
  // Enlight連携確認はアプリ管理者以上限定機能のため、権限がないユーザーにはタブ自体を表示しない
  { id: 'enlightSyncCheck', label: 'Enlight連携確認', link: '/enlight-sync-check', appAdminOnly: true },
];

const Tabs: React.FC<TabsProps> = ({ disabledTabs, disabledReasons }) => {
  const isAdmin = useIsAdmin();
  const isAppAdmin = useIsAppAdmin();
  const visibleTabs = tabs.filter((tab) => (!tab.adminOnly || isAdmin) && (!tab.appAdminOnly || isAppAdmin));

  // 現在のパスを取得
  const location = useLocation();

  // location.pathname と一致するタブのIDをアクティブタブとする
  //  ：貼り付けURLで末尾スラッシュ/子ルート/hash などが混ざっても active が付くように正規化する
  const effectivePathname = (() => {
    const rawPathname = String(location.pathname ?? '');

    // "/weekly-report/" のような末尾スラッシュを除去
    const trimmed = rawPathname.replace(/\/+$/, '') || '/';

    // HashRouter 等で "#/weekly-report?..." になるケースの吸収
    const rawHash = String((location as any).hash ?? '');
    if (rawHash.startsWith('#/')) {
      const withoutHash = rawHash.slice(1); // "/weekly-report?x=1"
      const pathOnly = withoutHash.split('?')[0]; // "/weekly-report"
      const trimmedHashPath = String(pathOnly).replace(/\/+$/, '') || '/';
      return trimmedHashPath;
    }

    return trimmed;
  })();

  // path 判定：配下ルートも active 扱いにする
  const activeTab = (() => {
    if (effectivePathname === '/') return 'inputEdit';
    if (effectivePathname === '/weekly-report' || effectivePathname.startsWith('/weekly-report/')) {
      return 'weekly';
    }
    if (effectivePathname === '/master-manage' || effectivePathname.startsWith('/master-manage/')) {
      return 'masterManage';
    }
    if (effectivePathname === '/classification-market' || effectivePathname.startsWith('/classification-market/')) {
      return 'classificationMarket';
    }
    if (effectivePathname === '/enlight-sync-check' || effectivePathname.startsWith('/enlight-sync-check/')) {
      return 'enlightSyncCheck';
    }
    return undefined;
  })();

  return (
    <div className="tabs-container">
      {visibleTabs.map(tab => {
        const disabled = Boolean(disabledTabs?.[tab.id]);
        const disabledReason = disabledReasons?.[tab.id];
        const button = (
          <button
            type="button"
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            disabled={disabled}
            aria-disabled={disabled}
            title={disabled ? disabledReason : undefined}
          >
            {tab.label}
          </button>
        );

        if (disabled) {
          return (
            <span key={tab.id} title={disabledReason}>
              {button}
            </span>
          );
        }

        return (
          <Link key={tab.id} to={tab.link}>
            {button}
          </Link>
        );
      })}
    </div>
  );
};

export default Tabs;
